import type { BoxProps, MotionPropsFor } from '@hover/blueprint';
import { Box, motion, useBreakpointValue, useSize } from '@hover/blueprint';
import type { MotionValue } from 'framer-motion';
import { useTransform } from 'framer-motion';
import React, { useMemo, useRef } from 'react';

type CarouselElementProps = {
  index: number;
  containerWidth: number;
  scrollX: MotionValue<number>;
  isLast?: boolean;
  peekValueOverrides?: { base?: number; tablet?: number; desktop?: number };
} & MotionPropsFor<BoxProps>;

const MotionBox = motion(Box);

export const PeekingCarouselElement = React.memo(
  ({
    children,
    index,
    containerWidth,
    scrollX,
    isLast,

    peekValueOverrides,
    ...props
  }: CarouselElementProps) => {
    const peekValue = useBreakpointValue({
      base: peekValueOverrides?.base || 24,
      tablet: peekValueOverrides?.tablet || 50,
      desktop: peekValueOverrides?.desktop || 120,
    });
    const ref = useRef(null);
    const size = useSize(ref);
    const i = index + 1;

    const width = size?.width ?? 0;
    const isFirst = index === 0;

    // Each element seems to be displaced by 8px for some reason.
    // I haven't see any reason why this would be the case on the projects page, so my
    // current guess that something about the scrollX fetched from motion is causing this.
    // For now, adding a cumulative displacement of 8px for each element seems to fix this behavior.
    const inputRange = [
      (index - 1) * (containerWidth + 8),
      index * (containerWidth + 8),
      i * (containerWidth + 8),
    ];
    const translateValue = useMemo(
      () => containerWidth / 2 - width / 2 + peekValue,
      [containerWidth, peekValue, width],
    );

    const outputRange = [
      isFirst ? 0 : -1 * translateValue,
      0,
      isLast ? 0 : translateValue,
    ];

    const translateX = useTransform(scrollX, inputRange, outputRange);

    return (
      <MotionBox
        alignSelf="flex-start"
        data-testid="peeking-carousel-element"
        height="100%"
        justifyContent="center"
        layout
        minWidth="100%"
        position="relative"
        scrollSnapAlign="start"
        viewport={{ amount: 0.95 }}
        {...props}
      >
        <MotionBox
          height="100%"
          ref={ref}
          style={{ x: translateX }}
          transition={{ type: 'tween' }}
        >
          {children}
        </MotionBox>
      </MotionBox>
    );
  },
);
